<template>
  <div class="mx-4">
    <section :class="`flex w-full items-center justify-between mb-4 ${mq.isMobile || mq.isTablet ? 'is-mobile' : ''}`">
      <div class="flex items-center gap-2">
        <ButtonTertiary
          v-if="current_profile.type !== PROFILE_TYPES.CUSTOMER"
          action="filter"
          data-test="notifications-filter"
          :hide_text="!!mq.isMobile"
          @onClick="toggle_filter = !toggle_filter"
        >
          <span>{{ $t('notification.labels.filter') }}</span>
        </ButtonTertiary>

        <ButtonTertiary
          action="sort"
          data-test="notification-sorting-dropdown"
          :hide_text="!!mq.isMobile"
          @onClick="togglePopover"
        >
          {{ $t(`sorting.${sorting_label}`) }}
        </ButtonTertiary>

        <Popover ref="popover">
          <DropdownOption
            data-test="sort-updated-at-desc"
            @click="$emit('setSortOrder', ['updated_at desc'])"
          >
            {{ $t('sorting.updated_at_desc') }}
          </DropdownOption>
          <DropdownOption
            data-test="sort-updated-at-asc"
            @click="$emit('setSortOrder', ['updated_at asc'])"
          >
            {{ $t('sorting.updated_at_asc') }}
          </DropdownOption>

          <DropdownOption
            data-test="sort-level-name-asc"
            @click="$emit('setSortOrder', ['notification_level_type desc', 'notification_level_name asc'])"
          >
            {{ is_SUP ? $t('sorting.notification_level_name_asc_sup') : $t('sorting.notification_level_name_asc') }}
          </DropdownOption>

          <DropdownOption
            data-test="sort-level-name-desc"
            @click="$emit('setSortOrder', ['notification_level_type desc', 'notification_level_name desc'])"
          >
            {{ is_SUP ? $t('sorting.notification_level_name_desc_sup') : $t('sorting.notification_level_name_desc') }}
          </DropdownOption>
        </Popover>
      </div>
      <div class="flex items-center gap-2">
        <div class="level-item">
          <label
            class="flex size-12 cursor-pointer flex-col items-center text-sm justify-center rounded-full bg-surface-100 hover:bg-surface-200"
            @click="toggle"
          >
            <FontAwesomeIcon
              aria-controls="overlay_menu"
              aria-haspopup="true"
              :icon="['far', 'ellipsis']"
              size="lg"
            />
          </label>
          <PMenu
            id="overlay_menu"
            ref="menu"
            :model="items"
            :popup="true"
          >
            <template #item="{ item, props }">
              <div
                v-bind="props.action"
                @click.stop="$emit('openNotificationSubgroupsAll')"
              >
                <div class="text-sm">
                  {{ item.label }}
                </div>
              </div>
            </template>
          </PMenu>
        </div>
      </div>
    </section>

    <section
      v-if="current_profile.type === PROFILE_TYPES.EMPLOYEE"
      class="is-flex mb-4"
      style="align-items: center"
    >
      <div style="flex-grow: 1; display: flex; align-items: center">
        <b-taglist>
          <FilterTag
            v-if="!q_project_id"
            :closable="false"
            data-test="all-projects-tag-filter"
            size="is-medium"
          >
            {{ $t('notification.labels.all') }}
          </FilterTag>
          <FilterTag
            v-if="q_project_id && project_selected"
            data-test="project-tag-filter"
            size="is-medium"
            @close="q_project_id = null"
          >
            {{ $truncate(project_selected.attributes.name, 20) }}
          </FilterTag>
          <FilterTag
            v-if="unit_selected"
            data-test="unit-tag-filter"
            size="is-medium"
            @close="[(q_unit_id = null)]"
          >
            {{ $truncate(unit_selected.attributes.name, 20) }}
          </FilterTag>
          <FilterTag
            v-if="!q_decision_type_id && showFilterDecisions"
            :closable="false"
            data-test="all-decisions-tag-filter"
            size="is-medium"
          >
            {{ $t('notification.labels.allDecisions') }}
          </FilterTag>
          <FilterTag
            v-if="q_decision_type_id && decision_type_selected && showFilterDecisions"
            data-test="decisions-tag-filter"
            size="is-medium"
            @close="q_decision_type_id = null"
          >
            {{ $truncate(decision_type_selected.attributes.name, 20) }}
          </FilterTag>
          <FilterTag
            v-if="q_is_unread_only"
            data-test="unread-only-tag-filter"
            size="is-medium"
            @close="q_is_unread_only = false"
          >
            {{ $t('notification.labels.unreadOnly') }}
          </FilterTag>
        </b-taglist>
      </div>
    </section>

    <section>
      <div
        v-if="toggle_filter"
        class="is-relative mb-4"
        :focusable="false"
      >
        <b-field :label="$t('notification.labels.status')">
          <b-field>
            <div class="buttons">
              <ToggleToken
                data-test="show-all-notifications"
                :is_toggled="!q_is_unread_only"
                @onClick="q_is_unread_only = false"
              >
                {{ $t('notification.labels.allStatuses') }}
              </ToggleToken>
              <ToggleToken
                data-test="show-unopen-notifications"
                :is_toggled="q_is_unread_only"
                @onClick="q_is_unread_only = !q_is_unread_only"
              >
                {{ $t('notification.labels.unreadOnly') }}
              </ToggleToken>
            </div>
          </b-field>
        </b-field>

        <b-field
          v-if="current_profile.type === PROFILE_TYPES.EMPLOYEE"
          grouped
        >
          <b-field
            expanded
            :label="$t('ticket.labels.project')"
          >
            <SelectProject
              :active="project_selected"
              @changeSelect="(p) => (q_project_id = $_.get(p, 'id'))"
            />
          </b-field>
          <b-field
            expanded
            :label="$t('ticket.labels.unit')"
            v-if="!is_SUP"
          >
            <SelectUnit
              :active="unit_selected"
              :disabled="!selectable_units"
              :project_id="q_project_id"
              @changeSelect="(u) => (q_unit_id = $_.get(u, 'id'))"
            />
          </b-field>
        </b-field>
        <div
          v-if="
            !showFilterDecisions &&
            $flipper.enabled($FT.DECISIONS_CORE) &&
            current_profile.type === PROFILE_TYPES.EMPLOYEE
          "
          style="margin-top: -6px"
        >
          <a
            data-test="show-decisions-filter"
            @click.stop="showFilterDecisions = true"
          >
            {{ $t('notification.labels.showDecisionFilter') }}
          </a>
        </div>

        <b-field
          v-else-if="showFilterDecisions"
          expanded
        >
          <template #label>
            {{ $t('decision.labels.decisions') }}
            <span class="has-text-weight-normal">
              <a
                style="float: right"
                @click.stop="[(showFilterDecisions = false), (q_decision_type_id = null)]"
              >
                {{ $t('notification.labels.hideDecisionFilter') }}
              </a>
            </span>
          </template>
          <SelectDecisionType
            :model-value="decision_type_selected"
            :project_id="q_project_id"
            :unit_id="q_unit_id"
            @changeSelect="
              (dt) => {
                q_decision_type_id = $_.get(dt, 'id');
              }
            "
          />
        </b-field>
      </div>
    </section>
  </div>
</template>

<script>
import ToggleToken from '@/app/shared_components/buttons/ToggleToken.vue';
import FilterTag from '@/app/shared_components/tags/FilterTag.vue';
import SelectProject from '@/app/shared_components/selects/SelectProject.vue';
import SelectUnit from '@/app/shared_components/selects/SelectUnit.vue';
import { mapActions, mapGetters } from 'vuex';
import { PROFILE_TYPES, PROJECT_TYPES } from '@/app/data/model_constants';
import SelectDecisionType from '@/app/shared_components/selects/SelectDecisionType.vue';
import _ from 'lodash-es';
import { useMqService } from '@/plugins/mq';
import { SIDEBAR_TYPE_NOTIFICATIONS } from '@/app/data/general_constants';

export default {
  name: 'NotificationsFilter',
  components: {
    SelectDecisionType,
    ToggleToken,
    SelectProject,
    SelectUnit,
    FilterTag,
  },

  props: {
    sort_order: { type: String, required: true },
    sort_order_type: { type: String, required: true },
  },

  emits: ['set-sort-order', 'fetchNotificationSubgroups', 'open-notification-subgroups-all'],

  setup() {
    const mq = useMqService();
    return { mq };
  },

  data() {
    return {
      initialized: false,
      q_is_unread_only: false,
      q_project_id: false,
      q_unit_id: false,
      q_decision_type_id: false,
      toggle_filter: false,
      PROFILE_TYPES,
      showFilterDecisions: false,
      items: [
        {
          label: this.$t('notification.actions.markAllAsRead'),
        },
      ],
    };
  },

  computed: {
    ...mapGetters([
      'current_client',
      'current_profile',
      'decision_types',
      'sidebar_prefs',
      'projects',
      'sidebar_type',
      'units',
    ]),

    selectable_units() {
      return !!this.q_project_id;
    },

    filters() {
      const decision_type_id = this.q_decision_type_id;
      const project_id = this.q_project_id;
      const unread = this.q_is_unread_only;
      const unit_id = this.q_unit_id;
      return {
        decision_type_id,
        project_id,
        unit_id,
        unread,
      };
    },

    project_selected() {
      if (!this.q_project_id) return null;
      return _.find(this.projects, ['id', this.q_project_id]);
    },

    is_SUP() {
      return this.current_client.attributes.project_type === PROJECT_TYPES.SUP;
    },

    unit_selected() {
      if (!this.q_unit_id) return null;
      return _.find(this.units, ['id', this.q_unit_id]);
    },

    decision_type_selected() {
      if (!this.q_decision_type_id) return null;
      return _.find(this.decision_types, ['id', this.q_decision_type_id]);
    },

    sorting_label() {
      return [this.sort_order_type, this.sort_order].join('_');
    },
  },

  watch: {
    filters: {
      async handler() {
        await this.fetchFiltersRecords();
        this.UPDATE_SIDEBAR_PREFS(this.filters);
        this.$emit('fetchNotificationSubgroups');
      },
    },
    sidebar_type: {
      async handler(value) {
        if (value === SIDEBAR_TYPE_NOTIFICATIONS && !this.initialized) {
          await this.fetchFiltersRecords();
          this.initialized = true;
        }
      },
    },
  },

  created() {
    this.q_project_id = this.sidebar_prefs.project_id;
    this.q_decision_type_id = this.sidebar_prefs.decision_type_id;
    this.q_is_unread_only = this.sidebar_prefs.unopen;
    this.q_unit_id = this.sidebar_prefs.unit_id;
  },

  methods: {
    ...mapActions(['FETCH_DECISION_TYPE', 'UPDATE_SIDEBAR_PREFS', 'INIT_SIDEBAR_PREFS', 'FETCH_PROJECT', 'FETCH_UNIT']),

    togglePopover(event) {
      this.$refs.popover.toggle(event);
    },

    toggle(event) {
      this.$refs.menu.toggle(event);
    },

    async fetchFiltersRecords() {
      if (this.q_decision_type_id) await this.FETCH_DECISION_TYPE({ id: this.q_decision_type_id });
      if (this.q_project_id) await this.FETCH_PROJECT({ id: this.q_project_id });
      if (this.q_unit_id) await this.FETCH_UNIT({ id: this.q_unit_id });
    },
  },
};
</script>
